import { type JSX } from 'react';
import { Link, useMatch } from 'react-router-dom';
import { Bell } from 'lucide-react';
import { useUnreadCount } from '../lib/use-notifications.ts';
import { type PracticeMembership } from '../api/me.ts';
import { useMe } from '../lib/use-me.ts';
import { useSignOut } from '../lib/use-sign-out.ts';
import { cn } from '../lib/utils.ts';
import { practiceRoleLabel } from '../lib/labels.ts';
import { useCaseScopedPractice, useScopedPractice } from '../lib/active-practice.tsx';
import { BrandLogo } from './BrandLogo.tsx';
import { MobileNavDrawer } from './MobileNavDrawer.tsx';
import { Avatar } from './ui/index.ts';

/**
 * The practitioner sub-badge reflects the user's credential state (F1):
 *  - a verified credential → "(verified)"
 *  - a claimed-only credential (self-attested, not yet verified) → "(self-certified)"
 *  - no credential → no sub-badge.
 * Returns the qualifier word, or null when no clinical credential is on record.
 */
function credentialQualifier(m: PracticeMembership): 'verified' | 'self-declared' | null {
  if (m.isVerifiedPractitioner) return 'verified';
  if (m.credentials.some((c) => c.status === 'claimed')) return 'self-declared';
  return null;
}

/**
 * "Owner · Practitioner (verified)" / "Owner · Practitioner (self-certified)" / "Owner"
 * for the header chip. The Practitioner sub-badge is shown for clinician-capable roles
 * (owner / practitioner) and annotated with the credential state so a self-attested
 * registration reads differently from an independently-verified one (F1/F2).
 */
function roleBadgeLabel(m: PracticeMembership): string {
  const titled = (s: string) => s.replace(/_/g, ' ').replace(/\b\w/, (c) => c.toUpperCase());
  // not-authorization: builds the role BADGE TEXT for a membership. Display only.
  const isClinicianRole = m.roleInPractice === 'owner' || m.roleInPractice === 'practitioner';
  const q = isClinicianRole ? credentialQualifier(m) : null;

  // not-authorization: label text.
  if (m.roleInPractice === 'practitioner') {
    return q ? `Practitioner (${q})` : 'Practitioner';
  }
  // support_worker reads "ABA Tutor" (product terminology); other roles title-cased.
  // not-authorization: label text ('ABA Tutor' is the product term for support_worker).
  const base = m.roleInPractice === 'support_worker' ? 'ABA Tutor' : titled(m.roleInPractice);
  const parts = [base];
  if (q) parts.push(`Practitioner (${q})`);
  return parts.join(' · ');
}

/**
 * The role line shown for a membership - #4. A client_contact membership (a
 * client portal account) reads "Client" rather than the internal
 * "Client contact" jargon; staff roles use the full role badge.
 */
function headerRoleLabel(m: PracticeMembership): string {
  // not-authorization: label text.
  if (m.roleInPractice === 'client_contact') return 'Client';
  return roleBadgeLabel(m);
}

function staffFirst(ms: readonly PracticeMembership[]): PracticeMembership[] {
  // not-authorization: label order.
  return [...ms].sort((a, b) => Number(a.roleInPractice === 'client_contact') - Number(b.roleInPractice === 'client_contact'));
}

/**
 * Authenticated top bar: drawer trigger, brand (below lg), identity —
 * which is the profile link — unread notifications badge, sign-out. No primary nav since
 * v7.100: the drawer carries it below lg and the rail above.
 * Rendered inside ProtectedLayout above the routed content.
 */
export function AppHeader(): JSX.Element {
  const unread = useUnreadCount();
  const onSignOut = useSignOut();
  const meQ = useMe();
  // The practice the URL names, falling back to the default where it names none. It read
  // the chosen practice until the switcher was retired; with nothing left to choose, that
  // became "whichever /me returned first" — so a two-practice member read "Owner" while
  // working inside the practice where they are a tutor.
  const { membership: routeMembership, fromRoute } = useScopedPractice();
  // A case route names no :practiceId, so the scoped answer would fall back to the DEFAULT.
  // Name the CASE's practice or NOTHING — unresolved is not an invitation to guess.
  const caseMembership = useCaseScopedPractice();
  const caseWild = useMatch('/cases/:caseId/*');
  const caseExact = useMatch('/cases/:caseId');
  const onCaseRoute = caseWild !== null || caseExact !== null;
  const membership = onCaseRoute ? caseMembership : routeMembership;
  // A page about no one practice (the dashboard) shows every role held, each practice on hover (owner 2026-10-28).
  const everyRole = !onCaseRoute && !fromRoute && (meQ.data?.memberships.length ?? 0) > 1
    ? staffFirst(meQ.data?.memberships ?? []) : null;

  // Borderless by design (owner-approved chrome, 2026-08-18): the rail is the shell's
  // structure, so a second hairline here reads as a drawn corner rather than as
  // hierarchy. bg-background, not bg-card, so the toolbar sits ON the page.
  return (
    <header className="sticky top-0 z-30 bg-background">
      {/* One row at every width (v7.100). This bar previously wrapped: it held
          the brand, up to six `md:hidden` nav pills, the identity block, Profile,
          the bell and Sign out in a `flex-wrap` container, and globals.css forces
          a 44px minimum on every control - roughly four rows of chrome at 375px
          before any content. The nav pills now live in MobileNavDrawer, and Sign
          out moves into that drawer's footer below lg. `min-w-0` + `truncate` on
          the identity block is what guarantees no wrap on a long display name. */}
      <div className="flex h-14 items-center gap-2 px-3 sm:px-4">
        <MobileNavDrawer />

        {/* lg:hidden - above lg the rail owns the brand, and showing it twice would
            put two "HapMetrix home" links in the same view. */}
        <Link to="/" aria-label="HapMetrix home" className="shrink-0 rounded-md lg:hidden">
          {/* Mark-only below sm so a long name never pushes the bar to two rows. */}
          <BrandLogo wordmarkClassName="hidden sm:inline" />
        </Link>

        {/* aria-label distinguishes this utility nav from the drawer/rail's
            "Primary" landmark, so both are announced for what they are. */}
        <nav aria-label="Account" className="ml-auto flex min-w-0 items-center justify-end gap-1">
          {/* #4: who is signed in - name + role, visible on mobile too. Staff show
              their practice role (+practice in the tooltip); portal users show an
              account role so they are never left without an identity line.

              The block IS the profile link (human testing, 2026-08-19): a separate
              lg+ text link reading "Profile" sat one tap from it, going to the same
              screen. The drawer keeps its own labelled item - an avatar is not a
              self-describing destination. */}
          {meQ.data && (
            <Link
              to="/profile"
              data-testid="nav-profile"
              aria-label={`Your profile, ${meQ.data.displayName}`}
              className={cn(
                'flex min-w-0 items-center rounded-md px-2 py-1',
                'hover:bg-accent hover:text-foreground',
              )}
              title={
                everyRole !== null
                  ? [meQ.data.displayName, ...everyRole.map((m) => `${headerRoleLabel(m)} at ${m.practiceName}`)].join(' · ')
                  : membership
                  ? `${meQ.data.displayName} · ${membership.practiceName} · ${roleBadgeLabel(membership)}`
                  : `${meQ.data.displayName} · ${practiceRoleLabel(meQ.data.role)}`
              }
            >
              <span data-testid="header-user" className="flex min-w-0 items-center gap-2">
                <Avatar name={meQ.data.displayName} seed={meQ.data.id} />
                <span className="flex min-w-0 flex-col justify-center leading-tight">
                  <span
                    data-testid="header-user-name"
                    className="max-w-[28vw] truncate text-xs font-medium text-foreground sm:max-w-[40vw]"
                  >
                    {meQ.data.displayName}
                  </span>
                  <span
                    data-testid="header-role-badge"
                    className="max-w-[28vw] truncate text-[11px] text-muted-foreground sm:max-w-[40vw]"
                  >
                    {everyRole !== null
                      ? [...new Set(everyRole.map(headerRoleLabel))].join(' · ')
                      : membership ? headerRoleLabel(membership) : practiceRoleLabel(meQ.data.role)}
                  </span>
                </span>
              </span>
            </Link>
          )}

          <Link
            to="/notifications"
            aria-label={unread > 0 ? `Notifications, ${unread} unread` : 'Notifications'}
            className={cn(
              'relative inline-flex shrink-0 items-center justify-center rounded-md p-2 text-muted-foreground',
              'hover:bg-accent hover:text-foreground',
            )}
          >
            <Bell className="h-5 w-5" aria-hidden="true" />
            {unread > 0 && (
              <span
                data-testid="unread-badge"
                className="absolute -right-0.5 -top-0.5 inline-flex min-w-[18px] items-center justify-center rounded-full bg-destructive px-1 text-[11px] font-medium leading-tight text-destructive-foreground"
              >
                {unread > 99 ? '99+' : unread}
              </span>
            )}
          </Link>

          <button
            type="button"
            onClick={onSignOut}
            className={cn(
              'hidden rounded-md px-3 py-2 text-sm text-muted-foreground lg:block',
              'hover:bg-accent hover:text-foreground',
            )}
          >
            Sign out
          </button>
        </nav>
      </div>
    </header>
  );
}
